import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const PermissionContext = createContext();

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user && user.id) {
      loadUserPermissions();
    } else {
      setPermissions([]);
      setUserRole(null);
      setLoading(false);
    }
  }, [user]);

  const loadUserPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // If user is admin, grant all permissions automatically
      if (user.role === 'admin') {
        setPermissions(['*']); // Wildcard permission
        setUserRole('admin');
        setLoading(false);
        return;
      }

      // For non-admin users, try to load permissions from backend
      // If this fails (e.g., during initial rollout), fall back to role-based defaults
      try {
        const response = await api.get(
          `/api/users/${user.id}/permissions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Combine role permissions and custom permissions
        const effectivePerms = response.data.effectivePermissions || [];
        setPermissions(effectivePerms);
      } catch (permError) {
        console.warn('Could not load user permissions, using role-based defaults:', permError);
        // Use basic permissions based on role
        setPermissions(getRoleBasedPermissions(user.role));
      }
      
      setUserRole(user.role);
      
    } catch (error) {
      console.error('Error in permission loading:', error);
      // Fallback to basic role-based permissions
      setPermissions(getRoleBasedPermissions(user.role));
      setUserRole(user.role);
    } finally {
      setLoading(false);
    }
  };

  // Fallback: Basic permissions based on old role system
  const getRoleBasedPermissions = (role) => {
    const basicPermissions = ['dashboard.view', 'policies.view', 'documents.view', 'claims.view'];
    
    if (role === 'admin') {
      return ['*'];
    } else if (role === 'governing_body') {
      return [...basicPermissions, 'companies.view', 'companies.create', 'branches.view'];
    } else if (role === 'company') {
      return [...basicPermissions, 'policies.create', 'policies.update', 'branches.view'];
    } else if (role === 'branch') {
      return [...basicPermissions, 'policies.create'];
    }
    
    return basicPermissions;
  };

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission name (e.g., 'users.create')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!permission) return true; // No permission required
    if (!user) return false; // Not logged in
    
    // Admin wildcard check
    if (permissions.includes('*')) return true;
    
    // Check exact permission match
    if (permissions.includes(permission)) return true;
    
    // Check for wildcard resource permissions (e.g., 'users.*')
    const [resource] = permission.split('.');
    if (permissions.includes(`${resource}.*`)) return true;
    
    return false;
  };

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} requiredPermissions - Array of permission names
   * @returns {boolean}
   */
  const hasAnyPermission = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.some(perm => hasPermission(perm));
  };

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} requiredPermissions - Array of permission names
   * @returns {boolean}
   */
  const hasAllPermissions = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.every(perm => hasPermission(perm));
  };

  /**
   * Check if user has a specific role
   * @param {string|string[]} roles - Role name or array of role names
   * @returns {boolean}
   */
  const hasRole = (roles) => {
    if (!roles) return true;
    if (!user || !user.role) return false;
    
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  /**
   * Reload user permissions (useful after permission changes)
   * Returns a promise so we can await it
   */
  const reloadPermissions = async () => {
    if (user && user.id) {
      setLoading(true);
      try {
        await loadUserPermissions();
        return true;
      } catch (error) {
        console.error('Failed to reload permissions:', error);
        return false;
      } finally {
        setLoading(false);
      }
    }
    return false;
  };

  /**
   * Force refresh permissions (bypasses cache)
   */
  const refreshPermissions = async () => {
    console.log('🔄 Refreshing user permissions...');
    const result = await reloadPermissions();
    if (result) {
      console.log('✅ Permissions refreshed successfully');
    } else {
      console.error('❌ Failed to refresh permissions');
    }
    return result;
  };

  const value = {
    permissions,
    userRole,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    reloadPermissions,
    refreshPermissions  // Expose this for manual refresh
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to access permission context
 * @returns {object} Permission context with checking functions
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Component wrapper to conditionally render based on permissions
 * Usage: <PermissionGuard permission="users.create"><Button>Create</Button></PermissionGuard>
 */
export function PermissionGuard({ 
  permission, 
  permissions, 
  requireAll = false,
  role,
  fallback = null,
  children 
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = usePermissions();

  // Check role first if specified
  if (role && !hasRole(role)) {
    return fallback;
  }

  // Check permissions
  if (permission && !hasPermission(permission)) {
    return fallback;
  }

  if (permissions) {
    const hasPerms = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
    
    if (!hasPerms) {
      return fallback;
    }
  }

  return children;
}

export default PermissionContext;

